<template>
  <div class="overview-tab">
    <div class="stats-banner">
      <div class="stat-card" v-if="app.appRate">
        <div class="stat-icon star">
          <span class="material-symbols-outlined">star</span>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ app.appRate || "N/A" }}</div>
          <div class="stat-label">{{ $t("Apps.rating") }}</div>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon download">
          <span class="material-symbols-outlined">download</span>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ app.appDownloadCount || "N/A" }}</div>
          <div class="stat-label">{{ $t("Apps.downloads") }}</div>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon size">
          <span class="material-symbols-outlined">data_usage</span>
        </div>
        <div class="stat-content">
          <div class="stat-value">
            {{ app.appSize || "Varies with device" }}
          </div>
          <div class="stat-label">{{ $t("Apps.size") }}</div>
        </div>
      </div>
    </div>

    <div
      v-if="
        app.appScreenshotsElements.hasScreenshots.status &&
        app.appScreenshotsElements.hasScreenshots.screenShots
      "
      class="screenshot-gallery"
    >
      <h2>{{ $t("Apps.screenshots") }}</h2>
      <div class="screenshot-container">
        <div class="screenshot-wrapper">
          <div
            v-for="(screenshot, index) in app.appScreenshotsElements
              .hasScreenshots.screenShots"
            :key="index"
            class="screenshot"
            @click="openPreview(screenshot.imgPath)"
          >
            <img
              :src="screenshot.imgPath"
              alt="App Screenshot"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="content-section">
      <h2>{{ $t("Apps.appDescriptions") }}</h2>
      <div class="description-cards">
        <div class="description-card">
          <div class="info-icon short">
            <span class="material-symbols-outlined">short_text</span>
          </div>
          <div class="info-content">
            <div class="info-label">{{ $t("Apps.shortDescription") }}</div>
            <div
              class="info-value"
              v-html="app.appShortDescription || 'N/A'"
            ></div>
          </div>
        </div>

        <div class="description-card long">
          <div class="info-icon long">
            <span class="material-symbols-outlined">description</span>
          </div>
          <div class="info-content">
            <div class="info-label">{{ $t("Apps.longDescription") }}</div>
            <div
              class="info-value"
              v-html="formattedContent(app.appLongDescription) || 'N/A'"
            ></div>
          </div>
        </div>

        <div
          v-if="!app.appShortDescription && !app.appLongDescription"
          class="no-description"
        >
          <span class="material-symbols-outlined">description_off</span>
          <p>{{ $t("Apps.noDescription") }}</p>
        </div>

        <div class="description-card whatsnew">
          <div class="info-icon whatsnew">
            <span class="material-symbols-outlined">newspaper</span>
          </div>
          <div class="info-content">
            <div class="info-label">{{ $t("Apps.whatsNew") }}</div>
            <div class="info-value" v-html="app.appWhatsNew || 'N/A'"></div>
          </div>
        </div>
      </div>
    </div>

    <div class="content-section">
      <h2>{{ $t("Apps.appInformation") }}</h2>
      <div class="info-cards">
        <div class="info-card">
          <div class="info-icon version">
            <span class="material-symbols-outlined">settings</span>
          </div>
          <div class="info-content">
            <div class="info-label">{{ $t("Apps.version") }}</div>
            <div class="info-value version-display">
              {{ displayVersion || "N/A" }}
              <button
                v-if="hasVersionHistory"
                @click="openModal"
                class="version-history-button"
                title="View version history"
              >
                <span class="material-symbols-outlined">history</span>
              </button>
            </div>
          </div>
        </div>

        <div class="info-card">
          <div class="info-icon age">
            <span class="material-symbols-outlined">verified_user</span>
          </div>
          <div class="info-content">
            <div class="info-label">{{ $t("Apps.ageRating") }}</div>
            <div class="info-value">{{ app.appAgeRating || "N/A" }}</div>
          </div>
        </div>

        <div class="info-card">
          <div class="info-icon category">
            <span class="material-symbols-outlined">category</span>
          </div>
          <div class="info-content">
            <div class="info-label">{{ $t("Apps.category") }}</div>
            <div class="info-value">{{ app.appCategory || "N/A" }}</div>
          </div>
        </div>

        <div class="info-card">
          <div class="info-icon size">
            <span class="material-symbols-outlined">data_usage</span>
          </div>
          <div class="info-content">
            <div class="info-label">{{ $t("Apps.size") }}</div>
            <div class="info-value">
              {{ app.appSize || "Varies with device" }}
            </div>
          </div>
        </div>

        <div class="info-card">
          <div class="info-icon price">
            <span class="material-symbols-outlined">payments</span>
          </div>
          <div class="info-content">
            <div class="info-label">{{ $t("Apps.price") }}</div>
            <div class="info-value">{{ app.appPrice || "N/A" }}</div>
          </div>
        </div>

        <div class="info-card">
          <div class="info-icon purchases">
            <span class="material-symbols-outlined">shopping_cart</span>
          </div>
          <div class="info-content">
            <div class="info-label">{{ $t("Apps.inAppPurchases") }}</div>
            <div
              class="info-value"
              v-html="formattedInAppPurchases"
              @click="handleValueClick($event)"
            ></div>
          </div>
        </div>

        <div class="info-card">
          <div class="info-icon package">
            <span class="material-symbols-outlined">package</span>
          </div>
          <div class="info-content">
            <div class="info-label">{{ $t("Apps.packageName") }}</div>
            <div class="info-value">{{ app.appStoreId || "N/A" }}</div>
          </div>
        </div>

        <div class="info-card">
          <div class="info-icon copyright">
            <span class="material-symbols-outlined">copyright</span>
          </div>
          <div class="info-content">
            <div class="info-label">{{ $t("Apps.copyright") }}</div>
            <div class="info-value">
              {{ app.appCopyright || "N/A" }}
            </div>
          </div>
        </div>

        <div class="info-card">
          <div class="info-icon compatibility">
            <span class="material-symbols-outlined">devices</span>
          </div>
          <div class="info-content">
            <div class="info-label">{{ $t("Apps.compatibility") }}</div>
            <div
              class="info-value"
              v-html="formattedCompatibility"
              @click="handleValueClick($event)"
            ></div>
          </div>
        </div>

        <div class="info-card">
          <div class="info-icon reviewscount">
            <span class="material-symbols-outlined">reviews</span>
          </div>
          <div class="info-content">
            <div class="info-label">{{ $t("Apps.reviews") }}</div>
            <div class="info-value">{{ app.appReviewsCount || "N/A" }}</div>
          </div>
        </div>

        <div class="info-card">
          <div class="info-icon languages">
            <span class="material-symbols-outlined">translate</span>
          </div>
          <div class="info-content">
            <div class="info-label">{{ $t("Apps.languages") }}</div>
            <div
              class="info-value"
              v-html="formattedLanguages"
              @click="handleValueClick($event)"
            ></div>
          </div>
        </div>

        <div
          class="info-card"
          v-if="
            app.appSupports != 'null' &&
            app.appSupports &&
            app.appSupports.length
          "
        >
          <div class="info-icon supports">
            <span class="material-symbols-outlined">extension</span>
          </div>
          <div class="info-content">
            <div class="info-label">{{ $t("Apps.supports") }}</div>
            <div class="info-value" @click="handleValueClick($event)">
              {{ app.appSupports.join(", ") }}
            </div>
          </div>
        </div>

        <div class="info-card">
          <div class="info-icon updatedon">
            <span class="material-symbols-outlined">update</span>
          </div>
          <div class="info-content">
            <div class="info-label">{{ $t("Apps.updated") }}</div>
            <div class="info-value">
              {{ formatDateToLocale(app.appUpdatedOn) || "N/A" }}
            </div>
          </div>
        </div>

        <div class="info-card">
          <div class="info-icon releasedon">
            <span class="material-symbols-outlined">new_releases</span>
          </div>
          <div class="info-content">
            <div class="info-label">{{ $t("Apps.relased") }}</div>
            <div class="info-value">
              {{ formatDateToLocale(app.appReleasedOn) || "N/A" }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="content-section">
      <h2>Developer</h2>
      <div class="info-cards">
        <div class="info-card">
          <div class="info-icon developer">
            <span class="material-symbols-outlined">account_circle</span>
          </div>
          <div class="info-content">
            <div class="info-label">{{ $t("Developers.developerName") }}</div>
            <div class="info-value">
              <span class="link" @click="showDeveloper" style="cursor: pointer">
                {{ developer.developerName || "N/A" }}
              </span>
            </div>
          </div>
        </div>

        <div class="info-card">
          <div class="info-icon store">
            <span class="material-symbols-outlined">store</span>
          </div>
          <div class="info-content">
            <div class="info-label">{{ $t("Developers.developerPage") }}</div>
            <div class="info-value">
              <span
                class="link"
                @click="goToStore(developer.developerUrl)"
                style="cursor: pointer"
              >
                {{ $t("Developers.viewOnStore") }}
                <span class="material-symbols-outlined" style="font-size: 16px"
                  >open_in_new</span
                >
              </span>
            </div>
          </div>
        </div>

        <div class="info-card">
          <div class="info-icon email">
            <span class="material-symbols-outlined">mail</span>
          </div>
          <div class="info-content">
            <div class="info-label">{{ $t("Apps.email") }}</div>
            <template v-if="developer.appDeveloperSupportEmail">
              <a
                :href="'mailto:' + developer.appDeveloperSupportEmail"
                class="link info-value"
              >
                {{ developer.appDeveloperSupportEmail }}
              </a>
            </template>
            <template v-else>
              <div class="info-value">{{ "N/A" }}</div>
            </template>
          </div>
        </div>

        <div class="info-card">
          <div class="info-icon website">
            <span class="material-symbols-outlined">language</span>
          </div>
          <div class="info-content">
            <div class="info-label">{{ $t("Apps.website") }}</div>
            <a
              :href="developer.appDeveloperSupportWebSite"
              target="_blank"
              rel="noopener"
              class="info-value link"
            >
              {{ $t("Apps.visitSupportPage") }}
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="preview-modal" v-if="previewImage" @click="closePreview">
    <div class="modal-content">
      <button class="close-button" @click="closePreview">
        <span class="material-symbols-outlined">close</span>
      </button>
      <img :src="previewImage" alt="Preview" />
    </div>
  </div>
  <Modal
    :isOpen="isVersionHistoryOpen"
    :modalTitle="$t('Apps.version')"
    :actionButton="false"
    @close="closeModal"
  >
    <div class="version-history">
      <div
        v-for="version in versionHistory"
        :key="version.versionDisplay"
        class="version-item"
      >
        <div class="version-header">
          <h3>{{ $t("Apps.version") }} {{ version.versionDisplay }}</h3>
          <span class="version-date">{{
            formatDate(version.releaseDate)
          }}</span>
        </div>
        <div class="version-notes" v-html="version.releaseNotes"></div>
        <div class="version-timestamp">
          {{ $t("Apps.relased") }}:
          {{ formatDateTime(version.releaseTimestamp) }}
        </div>
      </div>
    </div>
  </Modal>
</template>

<script>
import Modal from "@/components/custom/modals/ModalComponent.vue";
import { formattedContent } from "@/ultis/user/functions/contentFunctions";

const MAX_TEXT_LENGTH = 25;

export default {
  name: "OverviewTab",
  components: { Modal },
  props: {
    app: {
      type: Object,
      required: true,
    },
    developer: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isVersionHistoryOpen: false,
      expandedTexts: new Set(),
      content: "",
      previewImage: null,
    };
  },
  computed: {
    hasVersionHistory() {
      return (
        Array.isArray(this.app.appVersions) && this.app.appVersions.length > 0
      );
    },
    displayVersion() {
      if (this.hasVersionHistory) {
        return this.app.appVersions[0].versionDisplay;
      }
      return this.app.appVersions;
    },
    versionHistory() {
      return this.hasVersionHistory ? this.app.appVersions : [];
    },
    formattedInAppPurchases() {
      this.content = this.formattedContent(this.app.appInAppPurchases);
      return this.formatLongText(this.content, MAX_TEXT_LENGTH, "purchases");
    },
    formattedCompatibility() {
      return this.formatLongText(
        this.app.appCompatibility,
        MAX_TEXT_LENGTH,
        "compatibility"
      );
    },
    formattedLanguages() {
      return this.formatLongText(
        this.app.appLanguages,
        MAX_TEXT_LENGTH,
        "languages"
      );
    },
  },
  methods: {
    openPreview(imgPath) {
      if (!this.isDragging) {
        this.previewImage = imgPath;
        document.body.style.overflow = "hidden";
      }
    },
    closePreview() {
      this.previewImage = null;
      document.body.style.overflow = "";
    },
    goToStore(url) {
      window.open(url, "_blank");
    },
    showDeveloper() {
      const storeId = this.developer.developerUrlId;
      this.$router.push({
        name: "Developer",
        params: { storeId: storeId },
      });
    },
    formattedContent(content) {
      return formattedContent(content);
    },
    openModal() {
      this.isVersionHistoryOpen = true;
    },
    closeModal() {
      this.isVersionHistoryOpen = false;
    },
    formatDateToLocale(date) {
      if (!date) return "";
      const locale = localStorage.getItem("localeTime") || "en-US";
      return new Date(date).toLocaleDateString(locale);
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
    formatDateTime(timestamp) {
      return new Date(timestamp).toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    formatLongText(text, maxLength = MAX_TEXT_LENGTH, uniqueIdSuffix = "") {
      if (text === null || text === undefined) return "N/A";
      if (typeof text !== "string") return text;

      if (text.length <= maxLength) return text;

      const safeEncode = (str) => {
        const encoder = new TextEncoder();
        const data = encoder.encode(str);
        return btoa(String.fromCharCode(...data));
      };

      const uniqueId = safeEncode(text).substring(0, 10) + uniqueIdSuffix;
      const isExpanded = this.expandedTexts.has(uniqueId);

      if (isExpanded) {
        return `${text} <span class="read-more-link" data-id="${uniqueId}">(${this.$t(
          "Apps.showLess"
        )})</span>`;
      }

      return `${text.substring(
        0,
        maxLength
      )}... <span class="read-more-link" data-id="${uniqueId}">(${this.$t(
        "Apps.showMore"
      )})</span>`;
    },
    toggleText(uniqueId) {
      if (this.expandedTexts.has(uniqueId)) {
        this.expandedTexts.delete(uniqueId);
      } else {
        this.expandedTexts.add(uniqueId);
      }
      this.$forceUpdate();
    },
    handleValueClick(event) {
      const textId = event.target.dataset.id;
      this.toggleText(textId);
    },
  },
};
</script>

<style scoped>
/** Preview Modal */
.preview-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
}

.modal-content img {
  max-width: 100%;
  min-height: 55vh;
  overflow: hidden;
  max-height: 90vh;
  object-fit: contain;
}

.close-button {
  position: absolute;
  top: -40px;
  right: 0;
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 8px;
}

.close-button .material-symbols-outlined {
  font-size: 32px;
}

/** Screenshot gallery */
.screenshot-gallery {
  margin-bottom: 24px;
  background: white;
  padding: 16px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.screenshot-container {
  overflow-x: auto;
  white-space: nowrap;
  display: flex;
  gap: 12px;
  padding-bottom: 8px;
}

.screenshot-wrapper {
  display: flex;
  flex-wrap: nowrap;
}

.screenshot {
  flex: 0 0 auto;
  width: 180px;
  height: auto;
  border-radius: 8px;
  overflow: hidden;
  margin-right: 5px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.screenshot:hover {
  cursor: pointer;
}

.screenshot img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.screenshot-container::-webkit-scrollbar {
  height: 6px;
}

.screenshot-container::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 6px;
}
/** */
.link {
  color: #1a73e8;
  text-decoration: none;
}

.link:hover {
  text-decoration: underline;
}

.read-more-link {
  color: #1a73e8;
  cursor: pointer;
  font-size: 14px;
  margin-left: 4px;
  user-select: none;
}

.read-more-link:hover {
  text-decoration: underline;
}

.overview-tab {
  max-width: 1200px;
  margin: 0 auto;
}

.stats-banner {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 32px;
}

.stat-card {
  background: white;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-icon.star {
  background: rgb(248, 240, 191);
}

.stat-icon.download {
  background: #f0f1fe;
}

.stat-icon.age {
  background: #fff3e0;
}

.stat-icon .material-symbols-outlined {
  font-size: 24px;
}

.star .material-symbols-outlined {
  color: goldenrod;
}

.download .material-symbols-outlined {
  color: #3f51b5;
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 20px;
  color: #202124;
  line-height: 1.2;
}

.stat-label {
  font-size: 14px;
  color: #5f6368;
  margin-top: 4px;
}

.content-section {
  margin-bottom: 32px;
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

h2 {
  font-size: 20px;
  font-weight: 500;
  color: #202124;
  margin-bottom: 16px;
}

.description-cards {
  display: grid;
  gap: 16px;
}

.description-card {
  background: #f8f9fa;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.description-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.description-card.long {
  margin-top: 16px;
}

.info-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.info-icon .material-symbols-outlined {
  font-size: 24px;
}

.stat-icon.size {
  background: #e8f5e9;
  color: #43a047;
}

.info-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
}

.info-card {
  background: #f8f9fa;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: transform 0.2s ease;
}

.info-card:hover {
  transform: translateY(-2px);
}

.info-label {
  font-size: 14px;
  color: #5f6368;
  margin-bottom: 4px;
}

.info-value {
  font-size: 16px;
  font-weight: 400;
  color: #202124;
  word-break: break-word;
}

.link {
  color: #1a73e8;
  text-decoration: none;
}

.link:hover {
  text-decoration: underline;
}

.no-description {
  text-align: center;
  padding: 40px;
  background: #f8f9fa;
  border-radius: 16px;
  color: #5f6368;
}

.no-description .material-symbols-outlined {
  font-size: 48px;
  margin-bottom: 16px;
}

.no-description p {
  margin: 0;
  font-size: 16px;
}
.version-history {
  max-height: 70vh;
  overflow-y: auto;
}

.version-item {
  padding: 16px;
  border-bottom: 1px solid #eee;
}

.version-item:last-child {
  border-bottom: none;
}

.version-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.version-header h3 {
  margin: 0;
  font-size: 16px;
  color: #1a73e8;
}

.version-date {
  font-size: 14px;
  color: #666;
}

.version-notes {
  margin: 8px 0;
  font-size: 14px;
  line-height: 1.5;
  color: #333;
}

.version-timestamp {
  font-size: 12px;
  color: #666;
  margin-top: 8px;
}

.version-display {
  display: flex;
  align-items: center;
  gap: 8px;
}

.version-text {
  flex: 1;
}

.version-history-button {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #666;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  margin-left: auto;
}

.version-history-button:hover {
  background: #f0f0f0;
  color: #1a73e8;
}

.version-history-button .material-symbols-outlined {
  font-size: 18px;
}

.version-history::-webkit-scrollbar {
  width: 6px;
}

.version-history::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.version-history::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.version-history::-webkit-scrollbar-thumb:hover {
  background: #666;
  cursor: pointer;
}

@media (max-width: 768px) {
  .stat-card {
    padding: 14px;
  }

  .stat-value{
    font-size: 16px;
  }
  .info-value{
    font-size: 14px;
  }
}
</style>
